import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { ResultadoTraza, TrazaCompleta, TrazaResumen } from '@manejo-alcanza/contracts';
import { ErrorApi, resumenTrazasPatroclo, trazaPatroclo, trazasPatroclo } from '../api';
import { Boton, Card, Cargando, Error_, Tabla, Tile } from '../componentes/basicos';
import { NavPatroclo } from '../componentes/patroclo/NavPatroclo';
import { NOMBRES_NODO, NOMBRES_TOOL } from '../componentes/patroclo/nombres';

/**
 * Las trazas de Patroclo: **qué preguntó cada usuario, qué se consultó y cuánto
 * costó** (D-33, traspaso §5.1 y §6.1). Solo administrador y desarrollador: las
 * preguntas son de todos los usuarios. El proxy y Patroclo lo vuelven a exigir.
 *
 * Ninguna traza trae la respuesta ni los resultados de las consultas: solo la
 * pregunta, el camino y el gasto. Es para auditar y para saber cuánto cuesta,
 * no para leer conversaciones ajenas.
 *
 * Los filtros viven en la URL: abrir una traza y volver deja la lista como
 * estaba, y un enlace a «las de ayer con error» se puede pasar.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POR_PAGINA = 50;
/** El rango máximo que acepta Patroclo; más, y contesta 400. */
const DIAS_MAXIMOS = 366;

const RESULTADOS: Record<ResultadoTraza, { texto: string; clase: string }> = {
  RESPONDIDA: { texto: 'Respondida', clase: 'bg-acento-tenue text-acento' },
  RECHAZADA: { texto: 'Rechazada', clase: 'bg-neutro-tenue text-neutro' },
  ERROR: { texto: 'Error', clase: 'bg-alerta-tenue text-alerta' },
  FUERA_DE_SERVICIO: { texto: 'Tope diario', clase: 'bg-aviso-tenue text-aviso' },
  LIMITE_POR_USUARIO: { texto: 'Límite por usuario', clase: 'bg-aviso-tenue text-aviso' },
};

const CAMPO = 'min-h-[44px] w-full rounded-md border border-borde bg-superficie px-2 py-1 text-base sm:min-h-0 sm:text-sm';

export function PatrocloTrazas(): ReactNode {
  const [params, setParams] = useSearchParams();
  // Por omisión, los últimos 7 días: los mismos que Patroclo usa para el
  // resumen sin fechas. Se fijan aquí para que lista y resumen miren lo mismo.
  const hoy = hoyEnBogota();
  const desde = params.get('desde') ?? sumarDias(hoy, -6);
  const hasta = params.get('hasta') ?? hoy;
  const usuario = params.get('usuario') ?? '';
  const resultado = params.get('resultado') ?? '';

  const filtrar = (clave: string, valor: string): void => {
    const nuevos = new URLSearchParams(params);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    setParams(nuevos, { replace: true });
  };

  // Se dice antes de preguntar: un 400 de Patroclo por un rango malo llegaría
  // con su mensaje, pero esto es más claro y no gasta la petición.
  const problemaRango =
    desde > hasta
      ? '«Desde» va después de «hasta».'
      : diasEntre(desde, hasta) + 1 > DIAS_MAXIMOS
        ? `El rango puede ser de ${DIAS_MAXIMOS} días como mucho.`
        : null;

  const resumen = useQuery({
    queryKey: ['patroclo', 'trazas', 'resumen', desde, hasta],
    queryFn: () => resumenTrazasPatroclo({ desde, hasta }),
    enabled: problemaRango === null,
  });

  const lista = useInfiniteQuery({
    queryKey: ['patroclo', 'trazas', 'lista', desde, hasta, usuario, resultado],
    queryFn: ({ pageParam }) =>
      trazasPatroclo({
        desde,
        hasta,
        usuario_id: usuario || undefined,
        resultado: (resultado || undefined) as ResultadoTraza | undefined,
        limite: POR_PAGINA,
        antes_de: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.siguiente ?? undefined,
    enabled: problemaRango === null,
  });

  const trazas = lista.data?.pages.flatMap((p) => p.trazas) ?? [];
  const r = resumen.data;

  return (
    <div className="space-y-4">
      <NavPatroclo />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Trazas</h1>
          <p className="text-sm text-tenue">Qué preguntó cada usuario, qué se consultó y cuánto costó.</p>
        </div>
        {r && <GastoDeHoy hoy={r.hoy} />}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="text-xs text-tenue">
          Desde
          <input type="date" value={desde} max={hoy} onChange={(e) => filtrar('desde', e.target.value)} className={CAMPO} />
        </label>
        <label className="text-xs text-tenue">
          Hasta
          <input type="date" value={hasta} max={hoy} onChange={(e) => filtrar('hasta', e.target.value)} className={CAMPO} />
        </label>
        <label className="text-xs text-tenue">
          Usuario
          <select value={usuario} onChange={(e) => filtrar('usuario', e.target.value)} className={CAMPO}>
            <option value="">Todos</option>
            {/* Los que preguntaron algo en el período: los demás no tienen trazas. */}
            {r?.por_usuario.map((u) => (
              <option key={u.usuario_id} value={u.usuario_id}>
                {u.usuario_nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-tenue">
          Resultado
          <select value={resultado} onChange={(e) => filtrar('resultado', e.target.value)} className={CAMPO}>
            <option value="">Todos</option>
            {Object.entries(RESULTADOS).map(([clave, { texto }]) => (
              <option key={clave} value={clave}>
                {texto}
              </option>
            ))}
          </select>
        </label>
      </div>

      {problemaRango && <Error_>{problemaRango}</Error_>}

      {resumen.isLoading && <Cargando que="el resumen" />}
      {resumen.isError && <Error_>{mensajeDe(resumen.error, 'No se pudo cargar el resumen.')}</Error_>}
      {r && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Tile
              etiqueta="Preguntas"
              valor={entero(r.total.preguntas)}
              pie={`${entero(r.total.respondidas)} respondidas · ${entero(r.total.rechazadas)} rechazadas · ${entero(r.total.errores)} con error · ${entero(r.total.cortadas_por_limite)} cortadas por límite`}
            />
            <Tile
              etiqueta="Tokens"
              valor={entero(r.total.tokens_entrada + r.total.tokens_salida)}
              pie={`${entero(r.total.tokens_entrada)} de entrada · ${entero(r.total.tokens_salida)} de salida · ${entero(r.total.tokens_cache_lectura)} leídos de caché`}
            />
            <Tile
              etiqueta="Costo"
              valor={usd(r.total.costo_usd, r.total.medicion_completa)}
              pie={
                r.total.medicion_completa
                  ? `Del ${fechaCorta(r.desde)} al ${fechaCorta(r.hasta)}`
                  : NOTA_MEDICION
              }
            />
          </div>

          {/* Una debajo de la otra: cada tabla pide 720 px y lado a lado se cortaban. */}
          <div className="space-y-3">
            <Card titulo="Por usuario">
              {r.por_usuario.length === 0 ? (
                <p className="text-sm text-tenue">Nadie preguntó nada en este período.</p>
              ) : (
                <Tabla>
                  <thead>
                    <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                      <th className="px-3 py-2 font-medium">Usuario</th>
                      <th className="px-3 py-2 text-right font-medium">Preguntas</th>
                      <th className="px-3 py-2 text-right font-medium">Rechazadas</th>
                      <th className="px-3 py-2 text-right font-medium">Errores</th>
                      <th className="px-3 py-2 text-right font-medium">Tokens</th>
                      <th className="px-3 py-2 text-right font-medium">Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.por_usuario.map((u) => (
                      <tr key={u.usuario_id} className="border-b border-borde last:border-0">
                        <td className="px-3 py-2">
                          <div className="font-medium">{u.usuario_nombre}</div>
                          <div className="text-xs text-tenue">{u.rol}</div>
                        </td>
                        <td className="cifra px-3 py-2">{entero(u.preguntas)}</td>
                        <td className="cifra px-3 py-2">{entero(u.rechazadas)}</td>
                        <td className="cifra px-3 py-2">{entero(u.errores)}</td>
                        <td className="cifra px-3 py-2">{entero(u.tokens_entrada + u.tokens_salida)}</td>
                        <td className="cifra px-3 py-2">{usd(u.costo_usd, u.medicion_completa)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tabla>
              )}
            </Card>

            <Card titulo="Por consulta a ManejoAlcanza">
              {r.por_tool.length === 0 ? (
                <p className="text-sm text-tenue">Ninguna pregunta consultó datos en este período.</p>
              ) : (
                <Tabla>
                  <thead>
                    <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                      <th className="px-3 py-2 font-medium">Consulta</th>
                      <th className="px-3 py-2 text-right font-medium">Llamadas</th>
                      <th className="px-3 py-2 text-right font-medium">Errores</th>
                      <th className="px-3 py-2 text-right font-medium" title="El modelo la pidió y el rol no la tiene">
                        No ejecutadas
                      </th>
                      <th className="px-3 py-2 text-right font-medium">Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.por_tool.map((t) => (
                      <tr key={t.tool} className="border-b border-borde last:border-0">
                        <td className="px-3 py-2">
                          <div className="font-medium">{NOMBRES_TOOL[t.tool] ?? t.tool}</div>
                          <code className="text-xs text-tenue">{t.tool}</code>
                        </td>
                        <td className="cifra px-3 py-2">{entero(t.llamadas)}</td>
                        <td className="cifra px-3 py-2">{entero(t.errores)}</td>
                        <td className="cifra px-3 py-2">{entero(t.no_ejecutadas)}</td>
                        <td className="cifra px-3 py-2">{duracion(t.ms_promedio)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tabla>
              )}
            </Card>
          </div>
        </>
      )}

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Preguntas, la más reciente primero</h2>
        {lista.isLoading && <Cargando que="las trazas" />}
        {lista.isError && <Error_>{mensajeDe(lista.error, 'No se pudieron cargar las trazas.')}</Error_>}
        {lista.data && (
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                <th className="px-3 py-2 font-medium">Hora</th>
                <th className="px-3 py-2 font-medium">Usuario</th>
                <th className="px-3 py-2 font-medium">Pregunta</th>
                <th className="px-3 py-2 font-medium">Camino</th>
                <th className="px-3 py-2 font-medium">Resultado</th>
                <th className="px-3 py-2 text-right font-medium">Tokens</th>
                <th className="px-3 py-2 text-right font-medium">Costo</th>
                <th className="px-3 py-2 text-right font-medium">Tardó</th>
              </tr>
            </thead>
            <tbody>
              {trazas.map((t) => (
                <FilaTraza key={t.id} traza={t} />
              ))}
              {trazas.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-tenue">
                    No hay preguntas con estos filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </Tabla>
        )}
        {lista.hasNextPage && (
          <Boton tipo="secundario" onClick={() => void lista.fetchNextPage()} deshabilitado={lista.isFetchingNextPage}>
            {lista.isFetchingNextPage ? 'Cargando…' : 'Ver más'}
          </Boton>
        )}
      </section>
    </div>
  );
}

function FilaTraza({ traza: t }: { traza: TrazaResumen }): ReactNode {
  const { search } = useLocation();
  return (
    <tr className="border-b border-borde align-top last:border-0 hover:bg-fondo">
      <td className="whitespace-nowrap px-3 py-2 text-xs">{hora(t.creada_en)}</td>
      <td className="px-3 py-2">
        <div>{t.usuario_nombre}</div>
        <div className="text-xs text-tenue">{t.rol}</div>
      </td>
      <td className="max-w-xs px-3 py-2">
        {/* La fila lleva al detalle por la pregunta (DISENO.md, «Tabla»). */}
        {/* Los filtros viajan con el enlace: «← Trazas» vuelve a la lista como estaba. */}
        <Link
          to={`/patroclo/trazas/${t.id}`}
          state={{ filtros: search }}
          title={t.pregunta}
          className="font-medium text-acento hover:underline"
        >
          {recortar(t.pregunta, 90)}
        </Link>
      </td>
      <td className="px-3 py-2 text-xs">
        <div>{t.ruta ?? <span className="text-tenue">sin camino</span>}</div>
        {t.tools.length > 0 && <div className="text-tenue">{t.tools.join(', ')}</div>}
      </td>
      <td className="px-3 py-2">
        <PillResultado resultado={t.resultado} />
        {t.motivo_rechazo && <div className="mt-1 text-xs text-tenue">{t.motivo_rechazo}</div>}
      </td>
      <td className="cifra px-3 py-2">{entero(t.tokens)}</td>
      <td className="cifra px-3 py-2">{usd(t.costo_usd, t.medicion_completa)}</td>
      <td className="cifra px-3 py-2">{duracion(t.latencia_ms)}</td>
    </tr>
  );
}

/** «US$0,05 de US$5,00» y los tokens de hoy: lo que se ve antes que nada. */
function GastoDeHoy({ hoy }: { hoy: { costo_usd: string; tokens: number; tope_diario_usd: string | null; tope_diario_tokens: number | null } }): ReactNode {
  return (
    <div className="rounded-lg border border-borde bg-superficie px-3 py-2 text-sm shadow-suave">
      <div className="text-xs font-medium uppercase tracking-wide text-tenue">Hoy</div>
      <div>
        <strong className="cifra !text-left">{usd(hoy.costo_usd, true)}</strong>
        {hoy.tope_diario_usd !== null ? (
          <span className="text-tenue"> de {usd(hoy.tope_diario_usd, true)}</span>
        ) : (
          <span className="text-tenue"> · sin tope de gasto</span>
        )}
      </div>
      <div className="text-xs text-tenue">
        {entero(hoy.tokens)} tokens
        {hoy.tope_diario_tokens !== null && ` de ${entero(hoy.tope_diario_tokens)}`}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Una ejecución entera: por dónde pasó, qué consultó y qué costó cada llamada. */
export function PatrocloTraza(): ReactNode {
  const { id = '' } = useParams();
  const { state } = useLocation() as { state: { filtros?: string } | null };
  const valido = UUID.test(id);
  const traza = useQuery({
    queryKey: ['patroclo', 'traza', id],
    queryFn: () => trazaPatroclo(id),
    // Un id que no es UUID no existe: no se pregunta, y no se enseña el
    // «uuid is expected» del proxy.
    enabled: valido,
    retry: false,
  });

  const volver = (
    <Link to={`/patroclo/trazas${state?.filtros ?? ''}`} className="text-xs text-tenue underline">
      ← Trazas
    </Link>
  );

  if (!valido || (traza.error instanceof ErrorApi && (traza.error.estado === 404 || traza.error.estado === 400))) {
    return (
      <div className="space-y-4">
        <NavPatroclo />
        {volver}
        <Error_>Esa traza no existe.</Error_>
      </div>
    );
  }
  if (traza.isLoading) return <Cargando que="la traza" />;
  if (traza.isError || !traza.data) {
    return (
      <div className="space-y-4">
        <NavPatroclo />
        {volver}
        <Error_>{mensajeDe(traza.error, 'No se pudo abrir la traza.')}</Error_>
      </div>
    );
  }

  const t = traza.data;
  return (
    <div className="space-y-4">
      <NavPatroclo />
      <div>
        {volver}
        <h1 className="mt-1 text-lg font-semibold">{t.pregunta}</h1>
        <p className="font-mono text-xs text-tenue">ref. {t.id}</p>
      </div>

      <Card>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <Dato etiqueta="Cuándo" valor={hora(t.creada_en)} />
          <Dato etiqueta="Quién" valor={`${t.usuario_nombre} · ${t.rol}`} />
          <Dato etiqueta="Perfil" valor={t.perfil} />
          <Dato
            etiqueta="Resultado"
            valor={
              <>
                <PillResultado resultado={t.resultado} />
                {t.motivo_rechazo && <span className="ml-2 text-tenue">{t.motivo_rechazo}</span>}
              </>
            }
          />
          <Dato etiqueta="Camino" valor={t.ruta ?? 'sin camino'} />
          {/* El tipo, sin su mensaje: Patroclo no lo guarda. */}
          {t.error && <Dato etiqueta="Error" valor={<code className="text-alerta">{t.error}</code>} />}
          <Dato etiqueta="Tardó" valor={duracion(t.latencia_ms)} />
          <Dato
            etiqueta="Costo"
            valor={
              <span title={t.medicion_completa ? undefined : NOTA_MEDICION}>
                {usd(t.costo_usd, t.medicion_completa)}
              </span>
            }
          />
          <Dato
            etiqueta="Tokens"
            valor={`${entero(t.tokens_entrada)} entrada · ${entero(t.tokens_salida)} salida · ${entero(t.tokens_cache_lectura)} caché leída · ${entero(t.tokens_cache_escritura)} caché escrita`}
          />
          {t.conversacion_id && (
            <Dato
              etiqueta="Conversación"
              valor={
                <Link to={`/patroclo/${t.conversacion_id}`} className="text-acento underline">
                  Abrirla
                </Link>
              }
            />
          )}
        </dl>
        {!t.medicion_completa && <p className="mt-3 text-xs text-aviso">{NOTA_MEDICION}</p>}
      </Card>

      <Card titulo="Los pasos, en orden">
        {t.pasos.length === 0 ? (
          <p className="text-sm text-tenue">Esta ejecución no registró pasos.</p>
        ) : (
          <ol className="space-y-1 text-sm">
            {t.pasos.map((p, i) => (
              <li key={i} className="rounded border border-borde bg-fondo px-3 py-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium">{NOMBRES_NODO[p.nodo] ?? p.nodo}</span>
                  <code className="text-[0.7rem] text-tenue">{p.nodo}</code>
                  <span className="cifra ml-auto">{duracion(p.ms)}</span>
                </div>
                {p.detalle && <div className="mt-0.5 break-words text-xs text-tenue">{p.detalle}</div>}
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card titulo="Consultas a ManejoAlcanza">
        {t.detalle_tools.length === 0 ? (
          <p className="text-sm text-tenue">Esta pregunta no consultó datos.</p>
        ) : (
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                <th className="px-3 py-2 font-medium">Consulta</th>
                <th className="px-3 py-2 font-medium">Parámetros</th>
                <th className="px-3 py-2 font-medium">Cómo terminó</th>
                <th className="px-3 py-2 text-right font-medium">Tardó</th>
              </tr>
            </thead>
            <tbody>
              {t.detalle_tools.map((d, i) => (
                <tr key={i} className="border-b border-borde align-top last:border-0">
                  <td className="px-3 py-2">
                    <div className="font-medium">{NOMBRES_TOOL[d.tool] ?? d.tool}</div>
                    <code className="text-xs text-tenue">{d.tool}</code>
                  </td>
                  <td className="px-3 py-2">
                    {d.parametros && Object.keys(d.parametros).length > 0 ? (
                      <code className="whitespace-pre-wrap break-all text-xs">{JSON.stringify(d.parametros, null, 1)}</code>
                    ) : (
                      <span className="text-xs text-tenue">sin parámetros</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <ComoTermino herramienta={d} />
                  </td>
                  <td className="cifra px-3 py-2">{duracion(d.ms)}</td>
                </tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Card>

      <Card titulo="Llamadas al modelo">
        {t.llamadas_modelo.length === 0 ? (
          <p className="text-sm text-tenue">Esta pregunta no llamó al modelo.</p>
        ) : (
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                <th className="px-3 py-2 font-medium">Tarea</th>
                <th className="px-3 py-2 font-medium">Modelo</th>
                <th className="px-3 py-2 text-right font-medium">Entrada</th>
                <th className="px-3 py-2 text-right font-medium">Salida</th>
                <th className="px-3 py-2 text-right font-medium">Caché leída</th>
                <th className="px-3 py-2 text-right font-medium">Caché escrita</th>
                <th className="px-3 py-2 text-right font-medium">Costo</th>
              </tr>
            </thead>
            <tbody>
              {t.llamadas_modelo.map((l, i) => (
                <tr key={i} className="border-b border-borde last:border-0">
                  <td className="px-3 py-2">{l.tarea}</td>
                  <td className="px-3 py-2">
                    <code className="text-xs">{l.modelo}</code>
                  </td>
                  <td className="cifra px-3 py-2">{tokensONoSe(l.entrada)}</td>
                  <td className="cifra px-3 py-2">{tokensONoSe(l.salida)}</td>
                  <td className="cifra px-3 py-2">{tokensONoSe(l.cache_lectura ?? null)}</td>
                  <td className="cifra px-3 py-2">{tokensONoSe(l.cache_escritura ?? null)}</td>
                  <td className="cifra px-3 py-2">
                    {l.costo_usd === null ? (
                      <span className="text-xs italic text-tenue" title="Este modelo no tiene precio en Patroclo">
                        sin precio
                      </span>
                    ) : (
                      usd(l.costo_usd, true)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Card>
    </div>
  );
}

function ComoTermino({ herramienta: d }: { herramienta: TrazaCompleta['detalle_tools'][number] }): ReactNode {
  if (d.ejecutada === false) {
    return <span className="text-aviso">No se ejecutó: el rol no tiene esta consulta</span>;
  }
  if (d.ok) return <span className="text-acento">Bien</span>;
  return (
    <span className="text-alerta">
      Falló{d.codigo_error ? <> · <code className="text-xs">{d.codigo_error}</code></> : null}
    </span>
  );
}

function PillResultado({ resultado }: { resultado: ResultadoTraza }): ReactNode {
  const { texto, clase } = RESULTADOS[resultado];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[0.7rem] font-semibold ${clase}`}>{texto}</span>;
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs font-medium text-tenue">{etiqueta}</dt>
      <dd className="break-words">{valor}</dd>
    </div>
  );
}

// --- Formato ------------------------------------------------------------------

const NOTA_MEDICION =
  'Alguna llamada al modelo no reportó su uso o no tiene precio: el costo real es al menos este, no exactamente este.';

/**
 * US$ con coma decimal y **sin redondear de más** (§6.1): una pregunta cuesta
 * fracciones de centavo, y redondear a dos decimales la volvería «US$0,00».
 * Se trabaja sobre el texto que manda Patroclo, nunca con `float` (invariante
 * 6): se quitan los ceros de sobra, dejando al menos dos decimales.
 *
 * Con la medición incompleta va «≥»: es una cota inferior, no el total.
 */
function usd(valor: string, completa: boolean): string {
  const [entera = '0', decimales = ''] = valor.split('.');
  const recortados = decimales.replace(/0+$/, '').padEnd(2, '0');
  const conMiles = entera.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${completa ? '' : '≥ '}US$${conMiles},${recortados}`;
}

const ENTERO = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });
function entero(n: number): string {
  return ENTERO.format(n);
}

function tokensONoSe(n: number | null): ReactNode {
  return n === null ? (
    <span className="text-xs italic text-tenue" title="Esta llamada no reportó su uso">
      no reportó
    </span>
  ) : (
    entero(n)
  );
}

function duracion(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} s`;
}

const HORA = new Intl.DateTimeFormat('es-CO', {
  timeZone: 'America/Bogota',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
function hora(instante: string): string {
  return HORA.format(new Date(instante));
}

function fechaCorta(fecha: string): string {
  return `${fecha.slice(8, 10)}/${fecha.slice(5, 7)}`;
}

function recortar(texto: string, maximo: number): string {
  return texto.length > maximo ? `${texto.slice(0, maximo - 1)}…` : texto;
}

function mensajeDe(error: unknown, otro: string): string {
  return error instanceof ErrorApi ? error.message : otro;
}

/** Colombia es siempre UTC-5: sin horario de verano no hay caso raro que cuidar. */
function hoyEnBogota(): string {
  return new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** Días de calendario: se cuentan en UTC para que ningún cambio de hora los mueva. */
function sumarDias(fecha: string, dias: number): string {
  const d = new Date(`${fecha}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

function diasEntre(desde: string, hasta: string): number {
  return Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000);
}
